import { useCallback, useMemo, useState } from "react";
import { formatCompact } from "../lib/renderers.js";
import {
    COMMAND_COLUMNS,
    COMMAND_COLUMNS_CONTEXT,
    renderCommandCell,
    commandRowClassName,
} from "./CommandRow.jsx";
import DataTable from "./DataTable.jsx";
import Select from "./Select.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { EYEBROW_CLASS, NOTE_CLASS } from "../lib/ui.js";
import {
    CONTEXT_WINDOW_OPTIONS,
    CONTEXT_DIRECTION_OPTIONS,
    deltaInWindow,
} from "../lib/attribution.js";

const SORT_OPTIONS = [
    ["newest", "Newest first"],
    ["tool", "Tool A-Z"],
    ["oldest", "Oldest first"],
];

// User-facing toggle chips for the command log. Each chip maps 1:1 to a wire
// `event_type` value. The flattened type set must stay in lockstep with
// `COMMAND_EVENT_TYPES` in lib/apiClient.js (the analytics-API filter default).
export const COMMAND_EVENT_TYPE_OPTIONS = [
    { value: "ProcessStart",      label: "Process", types: ["ProcessStart"] },
    { value: "FileState",         label: "File",    types: ["FileState"] },
    { value: "FileDelete",        label: "Delete",  types: ["FileDelete"] },
    { value: "FileRename",        label: "Rename",  types: ["FileRename"] },
    { value: "DnsQuery",          label: "DNS",     types: ["DnsQuery"] },
    { value: "NetworkConnection", label: "Network", types: ["NetworkConnection"] },
];

// Every wire event_type the command log knows about, flattened across the
// chips above. Used for the default (all-on) selection, "Select all", and
// the group-toggle math below.
export const COMMAND_EVENT_TYPES_ALL = COMMAND_EVENT_TYPE_OPTIONS.flatMap((o) => o.types);

const COMMAND_LOG_MIN_WIDTH = 420;

// Walk up to the page-level scroll surface (Overview / Endpoint) so
// virtualization measures against the same surface the trace log uses.
function findCommandScrollRoot(element) {
    if (!element) return null;
    const host =
        element.closest("[data-content-scroll]") ||
        element.closest("[data-endpoint-content-scroll]");
    if (!host) return null;
    return (
        host.querySelector("[data-overlayscrollbars-viewport]") ||
        host.querySelector(".os-viewport") ||
        host
    );
}

export default function CommandLogPanel({
    commands,
    selectedId = "",
    onSelect = () => {},
    title = "Commands",
    searchPlaceholder = "Command, tool, session, endpoint",
    sort: sortProp,
    onSortChange,
    searchQuery: searchQueryProp,
    onSearchChange,
    onLoadMore,
    maxRows = null,
    loading = false,
    contextOn = false,
    onContextToggle,
    contextWindowMs = 60_000,
    onContextWindowChange,
    contextDirection = "after",
    onContextDirectionChange,
    contextFocalOnly = false,
    onContextFocalOnlyChange,
    focalTraceId = "",
    focalTraceTimestamp = 0,
    attributionByCommand = null,
    traceLookup = null,
    onOpenTrace,
    eventTypes,
    onEventTypesChange,
    // Role-aware denial copy (from analyticsErrorCopy) when the raw
    // getCommands read is refused. Rendered in place of the "no commands
    // match" empty state so a denied role gets an honest reason, not a
    // misleading "widen the date range" prompt.
    denial = null,
}) {
    const selectedEventTypes = Array.isArray(eventTypes)
        ? eventTypes
        : COMMAND_EVENT_TYPES_ALL;
    // A chip is active when all of its wire types are selected; toggling
    // flips the whole group at once.
    const isOptionActive = (opt) =>
        opt.types.every((t) => selectedEventTypes.includes(t));
    const toggleEventType = useCallback(
        (opt) => {
            if (!onEventTypesChange) return;
            const active = opt.types.every((t) => selectedEventTypes.includes(t));
            const next = active
                ? selectedEventTypes.filter((t) => !opt.types.includes(t))
                : [...new Set([...selectedEventTypes, ...opt.types])];
            onEventTypesChange(next);
        },
        [selectedEventTypes, onEventTypesChange],
    );
    const setAllEventTypes = useCallback(
        () => onEventTypesChange?.(COMMAND_EVENT_TYPES_ALL),
        [onEventTypesChange],
    );
    const allSelected = COMMAND_EVENT_TYPES_ALL.every((t) => selectedEventTypes.includes(t));
    const [localQuery, setLocalQuery] = useState("");
    const [localSort, setLocalSort] = useState("newest");
    const sort = sortProp ?? localSort;
    const setSort = onSortChange ?? setLocalSort;
    const query = searchQueryProp ?? localQuery;
    const setQuery = onSearchChange ?? setLocalQuery;

    // Context mode is only meaningful when a trace is focal. Without
    // one, the toggle hides and the log behaves like a plain scoped
    // listing (no fade, no chips, no extra column).
    const contextAvailable = Boolean(focalTraceId);
    const contextActive = contextAvailable && contextOn;

    const cols = useDataTableColumns({
        columns: contextActive ? COMMAND_COLUMNS_CONTEXT : COMMAND_COLUMNS,
        defaultVisible: (contextActive ? COMMAND_COLUMNS_CONTEXT : COMMAND_COLUMNS).map((c) => c.id),
    });

    // Per-command lookup of attribution probability + other-trace links.
    // Used for the context filter, the fade ramp, and the per-row chips.
    const perCommandContext = useCallback(
        (cmdId) => {
            if (!attributionByCommand) return null;
            return attributionByCommand.get(cmdId) || null;
        },
        [attributionByCommand],
    );

    // The displayed set is the union of:
    //   - anchor commands: within focalTrace.timestamp ± window
    //   - context commands: attributed to focalTrace with prob > 0
    // When context is off (or no focal), the full scoped set passes
    // through unchanged.
    const visibleCommands = useMemo(() => {
        if (!contextActive || !focalTraceTimestamp) return commands;
        const out = [];
        for (const cmd of commands) {
            const deltaMs = cmd.timestamp - focalTraceTimestamp;
            const inWindow = deltaInWindow(deltaMs, contextDirection, contextWindowMs);
            const entry = perCommandContext(cmd.id);
            const focalLink = entry?.links.find((l) => l.traceId === focalTraceId);
            // For attribution-side inclusion, honor direction too — a "+"
            // window should never pull in pre-prompt events even if the
            // attribution model gave them a small score, and vice versa.
            const attributed = focalLink ? (
                contextDirection === "before" ? focalLink.deltaMs <= 0
                : contextDirection === "after"  ? focalLink.deltaMs >= 0
                : true
            ) : false;
            if (!(inWindow || attributed)) continue;
            // "Focal only" filters to commands where the focal trace is
            // the *top* attributed prompt — i.e. focal owns the event by
            // majority probability, even if weaker secondary links exist
            // as chips. Strict "no other links at all" was tempting but
            // matches ~0 in real session data (attribution typically
            // keeps the top 5 candidates, so chip-free rows are rare).
            if (contextFocalOnly) {
                if (!entry || entry.topTraceId !== focalTraceId) continue;
            }
            out.push(cmd);
        }
        return out;
    }, [contextActive, focalTraceTimestamp, focalTraceId, contextWindowMs, contextDirection, contextFocalOnly, commands, perCommandContext]);

    const trueTotal =
        typeof commands?.total === "number" ? commands.total : commands.length;
    const totalIsExact = commands?.totalIsExact !== false;
    const canLoadMore = Boolean(onLoadMore && visibleCommands.length < trueTotal);
    const maxRowsReached = Number.isFinite(maxRows)
        && commands.length >= maxRows
        && !totalIsExact;
    const failedCount = useMemo(
        () => visibleCommands.filter((c) => c.exitCode !== 0).length,
        [visibleCommands],
    );

    const renderCell = useCallback(
        (cmd, col) => renderCommandCell(cmd, col, {
            contextActive,
            focalTraceId,
            attributionEntry: contextActive ? perCommandContext(cmd.id) : null,
            traceLookup,
            onOpenTrace,
        }),
        [contextActive, focalTraceId, perCommandContext, traceLookup, onOpenTrace],
    );

    const rowClassName = useCallback(
        (cmd) => commandRowClassName({ active: cmd.id === selectedId }),
        [selectedId],
    );

    // Context-only rows (attributed but outside the temporal window)
    // fade so anchor rows read first. Avoids touching opacity in the
    // default (non-context) mode.
    const rowStyle = useCallback(
        (cmd) => {
            if (!contextActive || !focalTraceTimestamp) return null;
            const deltaMs = cmd.timestamp - focalTraceTimestamp;
            const inWindow = deltaInWindow(deltaMs, contextDirection, contextWindowMs);
            if (inWindow) return null;
            return { opacity: 0.55 };
        },
        [contextActive, focalTraceTimestamp, contextWindowMs, contextDirection],
    );

    const onRowClick = useCallback((cmd) => onSelect(cmd.id), [onSelect]);

    const getRowId = useCallback((cmd) => cmd.id, []);

    return (
        <>
            {title ? (
                <div className="grid gap-[2px]">
                    <span className={EYEBROW_CLASS}>Execution log</span>
                    <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em]">
                        {title}
                    </h2>
                </div>
            ) : null}

            <div className="grid gap-[8px]">
                <div
                    className="sticky z-20 grid gap-[8px] bg-[color:var(--bg)] pt-[8px] pb-[8px] shadow-[0_1px_0_var(--line-soft)] max-[900px]:static max-[900px]:shadow-none"
                    style={{ top: 0 }}
                >
                    <div className="grid items-end gap-[8px] [grid-template-columns:minmax(0,1fr)_152px] max-[900px]:grid-cols-1">
                        <label className="grid gap-[5px]">
                            <span className={NOTE_CLASS}>Search</span>
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={searchPlaceholder}
                                className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                            />
                        </label>
                        <label className="grid gap-[5px]">
                            <span className={NOTE_CLASS}>Sort</span>
                            <Select
                                value={sort}
                                onChange={setSort}
                                options={SORT_OPTIONS.map(([v, l]) => ({ value: v, label: l }))}
                                ariaLabel="Sort commands"
                            />
                        </label>
                    </div>

                    {onEventTypesChange && (
                        <div
                            role="group"
                            aria-label="Event types"
                            className="flex flex-wrap items-center gap-[5px]"
                        >
                            {COMMAND_EVENT_TYPE_OPTIONS.map((opt) => {
                                const active = isOptionActive(opt);
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        role="switch"
                                        aria-checked={active}
                                        onClick={() => toggleEventType(opt)}
                                        className={[
                                            "inline-flex items-center rounded-[2px] border px-[7px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
                                            active
                                                ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                                                : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                                        ].join(" ")}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                            {!allSelected && (
                                <button
                                    type="button"
                                    onClick={setAllEventTypes}
                                    className="ml-auto inline-flex items-center text-[10px] leading-[1.2] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                                >
                                    Select all
                                </button>
                            )}
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                        <span>
                            <strong className="text-[color:var(--ink)]">{visibleCommands.length}</strong>{" "}
                            shown
                        </span>
                        <span>
                            <strong className="text-[color:var(--ink)]">
                                {formatCompact(trueTotal)}
                                {totalIsExact ? "" : "+"}
                            </strong>{" "}
                            total
                        </span>
                        {failedCount > 0 && (
                            <span>
                                <strong className="text-red-400">{failedCount}</strong> failed
                            </span>
                        )}
                        {loading ? (
                            <span
                                className="inline-flex items-center gap-[5px] text-[color:var(--muted-soft)]"
                                aria-live="polite"
                            >
                                <span
                                    className="h-[6px] w-[6px] animate-pulse rounded-full bg-[color:var(--green)]"
                                    aria-hidden="true"
                                />
                                Loading
                            </span>
                        ) : null}
                        {contextAvailable && onContextToggle && (
                            <span className="ml-auto inline-flex items-stretch gap-[4px]">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!contextOn}
                                    onClick={() => onContextToggle(!contextOn)}
                                    className={[
                                        "inline-flex items-center gap-[5px] rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
                                        contextOn
                                            ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                                            : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                                    ].join(" ")}
                                >
                                    Context
                                </button>
                                {contextOn && onContextDirectionChange && (
                                    <span
                                        role="radiogroup"
                                        aria-label="Context window direction"
                                        className="inline-flex items-stretch rounded-[2px] border border-[color:var(--line)] overflow-hidden"
                                    >
                                        {CONTEXT_DIRECTION_OPTIONS.map((opt, i) => {
                                            const active = contextDirection === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={active}
                                                    aria-label={opt.aria}
                                                    onClick={() => onContextDirectionChange(opt.value)}
                                                    className={[
                                                        "px-[7px] py-[2px] text-[11px] leading-[1.0] font-medium tabular-nums tracking-normal transition-colors cursor-pointer",
                                                        i > 0 ? "border-l border-[color:var(--line)]" : "",
                                                        active
                                                            ? "bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                                                            : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                                                    ].join(" ")}
                                                >
                                                    {opt.glyph}
                                                </button>
                                            );
                                        })}
                                    </span>
                                )}
                                {contextOn && onContextWindowChange && (
                                    <Select
                                        value={contextWindowMs}
                                        onChange={(v) => onContextWindowChange(Number(v))}
                                        options={CONTEXT_WINDOW_OPTIONS}
                                        variant="chip"
                                        ariaLabel="Context window"
                                    />
                                )}
                                {contextOn && onContextFocalOnlyChange && (
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={!!contextFocalOnly}
                                        onClick={() => onContextFocalOnlyChange(!contextFocalOnly)}
                                        title="Hide commands that also match other prompts in scope"
                                        className={[
                                            "inline-flex items-center gap-[5px] rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
                                            contextFocalOnly
                                                ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                                                : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                                        ].join(" ")}
                                    >
                                        Focal only
                                    </button>
                                )}
                            </span>
                        )}
                    </div>

                    {/* Standalone column header pinned with the toolbar
                        (DataTable's showHeader=false keeps it out of
                        the rows region). Same pattern TraceLogPanel uses. */}
                    <div className="overflow-hidden">
                        <div
                            className="will-change-transform"
                            style={{ minWidth: `${COMMAND_LOG_MIN_WIDTH}px` }}
                        >
                            <div
                                className="grid items-center gap-x-2 border-y border-[color:var(--line-soft)] px-[6px] py-[4px] text-[9px] leading-[1.08] tracking-[0.08em] text-[color:var(--muted)] uppercase"
                                style={{ gridTemplateColumns: cols.gridCols }}
                            >
                                {cols.effective.map((c) => (
                                    <span
                                        key={c.id}
                                        className={
                                            c.id === "status" || c.id === "duration"
                                                ? "justify-self-end"
                                                : ""
                                        }
                                    >
                                        {c.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {visibleCommands.length ? (
                    <div
                        data-trace-log-scroll
                        className="min-w-0"
                        aria-busy={loading ? "true" : "false"}
                    >
                        <DataTable
                            cols={cols}
                            rows={visibleCommands}
                            getRowId={getRowId}
                            renderCell={renderCell}
                            loading={loading}
                            showHeader={false}
                            findScrollContainer={findCommandScrollRoot}
                            minWidth={`${COMMAND_LOG_MIN_WIDTH}px`}
                            onRowClick={onRowClick}
                            rowClassName={rowClassName}
                            rowStyle={rowStyle}
                            footer={
                                canLoadMore || maxRowsReached ? (
                                    <div className="grid place-items-center gap-[6px] pt-[8px] pb-[4px]">
                                        {canLoadMore ? (
                                            <button
                                                type="button"
                                                onClick={onLoadMore}
                                                disabled={loading}
                                                className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 cursor-pointer"
                                            >
                                                Load more{totalIsExact ? ` · ${formatCompact(trueTotal - visibleCommands.length)} remaining` : ""}
                                            </button>
                                        ) : null}
                                        {maxRowsReached ? (
                                            <span className={NOTE_CLASS} role="status">
                                                Showing the first {formatCompact(maxRows)} commands. Narrow the date range or add a filter to see more.
                                            </span>
                                        ) : null}
                                    </div>
                                ) : null
                            }
                        />
                    </div>
                ) : loading ? (
                    <div
                        className="grid gap-[6px] py-[12px]"
                        aria-busy="true"
                        aria-live="polite"
                    >
                        {Array.from({ length: 6 }, (_, i) => (
                            <div
                                key={i}
                                className="h-[32px] rounded-[2px] bg-[color:var(--line-soft)]"
                                style={{ opacity: 0.7 - i * 0.08 }}
                            />
                        ))}
                    </div>
                ) : denial ? (
                    // Raw-analytics denial (an aggregate-only role hitting this
                    // row-level event query). Render the role-aware copy — never
                    // the misleading "widen the date range" empty state, and
                    // never the raw gRPC status the customer must not see.
                    <div
                        role="alert"
                        className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]"
                    >
                        <strong className="text-[13px] leading-[1.08] font-medium">
                            {denial.message}
                        </strong>
                    </div>
                ) : (
                    <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]">
                        <strong className="text-[13px] leading-[1.08] font-medium">
                            No commands match this search
                        </strong>
                        <span className={NOTE_CLASS}>
                            Clear the search or widen the date range.
                        </span>
                    </div>
                )}
            </div>
        </>
    );
}
